import React from 'react';
import { AustrianPubMedArticle } from '../types';
import AdaptiveTitle from './AdaptiveTitle';
import { getRandomTopicImage } from '../utils/randomTopicImage';

interface AustrianArticleCardProps {
    article: AustrianPubMedArticle;
    onRubricClick?: (rubricId: number) => void;
    onReadStudy: (pmid: string, isAustrian: boolean) => void;
    getTranslation: (article: AustrianPubMedArticle) => any;
    getImageUrl?: (name: string | undefined, size?: 'big' | 'small') => string; // @deprecated Use random topic images instead
    rubricName?: string; // optional fallback rubric name from context
    t: (key: string) => string;
}

export const AustrianArticleCard: React.FC<AustrianArticleCardProps> = ({
    article,
    onRubricClick,
    onReadStudy,
    getTranslation,
    getImageUrl,
    rubricName,
    t
}) => {
    // Memoize the translation to prevent unnecessary re-renders
    const translation = React.useMemo(() => 
        getTranslation(article), [article, getTranslation]
    );

    // Memoize authors display
    const authorDisplay = React.useMemo(() => {
        if (!article.authors?.length) return '';
        return article.authors.slice(0, 2).join(', ') + 
               (article.authors.length > 2 ? ' et al.' : '');
    }, [article.authors]);

    // Use random topic image instead of backend-loaded images
    const [randomImage] = React.useState(() => getRandomTopicImage());

    return (
        <div className="bg-[#cde1ee] p-4 relative min-h-[350px] xl:h-[420px] flex flex-col overflow-hidden">
            <div className="flex flex-1 min-h-0">
                <div className="flex-1 flex flex-col min-h-0">
                    {/* Show rubric if available, otherwise show journal */}
                    {article.rubric ? (
                        <button 
                            onClick={() => article.rubric?.id && onRubricClick && 
                                onRubricClick(article.rubric.id)}
                            className="px-2 py-1 rounded text-xs xl:text-base bg-[#EF3340] text-white hover:bg-[#d12833] transition-colors shrink-0 inline-block"
                        >
                            {article.rubric.name || rubricName || ''}
                        </button>
                    ) : (
                        <div className="px-2 py-1 rounded text-xs xl:text-base bg-[#EF3340] text-white inline-block shrink-0">
                            {rubricName || article.journal?.name || t('pubmed.austrianJournal')}
                        </div>
                    )}
                    <div className="flex-1 min-h-0 mt-2">
                        <AdaptiveTitle
                            text={translation?.translatedTitle || article.title}
                            baseClassName="h-full"
                            maxFontSize={20}
                            minFontSize={11}
                        />
                    </div>
                    <div className="text-xs xl:text-sm text-[#111D4E] mt-2 shrink-0">
                        {authorDisplay}
                    </div>
                </div>
                {/* Show random topic image */}
                {article.rubric && randomImage && (
                    <img
                        src={randomImage}
                        alt={article.rubric.name || rubricName || ''}
                        className="w-16 h-16 sm:w-20 sm:h-20 object-contain ml-4 shrink-0" 
                    />
                )}
            </div>
            <button
                onClick={() => onReadStudy(article.pmid, true)}
                className="inline-flex items-center justify-center h-12 px-4 py-2 mt-2 text-base xl:text-lg text-white font-bold bg-gradient-to-r from-[#49ABDB] to-[#A0C9DA] hover:opacity-90 transition-opacity shrink-0"
            >
                {t('pubmed.readAustrianStudy')}
            </button>
        </div>
    );
};
